import { ModuleWithProviders, NgModule, InjectionToken } from '@angular/core';
import { IModuleOptions, ICustomDimensions } from './google-tag-manager-interfaces';
import { GoogleAnalyticsLibService } from './google-tag-manager.service';
import { TagManagerDataLayerService } from './tag-manager-data-layer.service';

/**
 * This is the main library module. By importing this library module in to your application module and calling it's `forRoot()` method,
 * your application will have the necessary services provided to it automatically. Additionally, any options can be passed in to the
 * `forRoot()` method.
 *
 * @param defaultDimensions A set of dimensions that will always be sent on every push and the dimensions state will be restored to on
 * every clear. If you will be assigning the dimension a value later, the default value should be `undefined`.
 */

/**
 * An intermediary injectable used to provide the raw config argument's value to the factory function
 * without causing AOT failures. NOTE: This value has to be exported otherwise the AoT compiler won't see it.
 */
export const TAG_MANAGER_MODULE_OPTIONS_TOKEN = new InjectionToken<IModuleOptions>('TAG_MANAGER_MODULE_OPTIONS_TOKEN');

/**
 * **This should return an ICustomDimensions object, but using that type currently breaks compodoc.
 *
 * A simple factory that will get its options value from the above token and create an options object.
 * "In order to play nicely with the AoT compiler, you have to extract the factory function declaration out
 * as a top-level exported value on the JavaScript module."
 * @see https://www.bennadel.com/blog/3565-providing-module-configuration-using-forroot-and-ahead-of-time-compiling-in-angular-7-2-0.htm
*/
export function TagManagerDefaultDimensionsFactory (options: IModuleOptions = {}) {
    return (options && options.defaultDimensions) ? options.defaultDimensions : undefined;
}

export function TagManagerFilterEmailFactory (options: IModuleOptions = {}): boolean {
    return (options && options.filters && options.filters.email) ? options.filters.email : true;
}

export function TagManagerFilterPhoneFactory (options: IModuleOptions = {}): boolean {
    return (options && options.filters && options.filters.phone) ? options.filters.phone : true;
}

export function TagManagerFilterQueryFactory (options: IModuleOptions = {}): boolean {
    return (options && options.filters && options.filters.query) ? options.filters.query : true;
}

@NgModule({
    imports: [ ],
    declarations: [ ],
    providers: [
        GoogleAnalyticsLibService,
        TagManagerDataLayerService
    ],
    exports: [ ]
})
export class GoogleAnalyticsLibModule {
    /**
   * When importing this module, call this method to automatically provide any necessary services.
   * This method's parameter are the options for the module.
   *
   * @param options The options for the library.
   *
   * @example
   * imports: [
   *   GoogleAnalyticsLibModule.forRoot({})
   *   ...
   * ]
   */
    static forRoot (options: IModuleOptions = {
        defaultDimensions: {},
        filters: { email: true, phone: true, query: true }
    }): ModuleWithProviders<GoogleAnalyticsLibModule> {
        return {
            ngModule: GoogleAnalyticsLibModule,
            providers: [
                GoogleAnalyticsLibService,
                TagManagerDataLayerService,
                {
                    provide: TAG_MANAGER_MODULE_OPTIONS_TOKEN,
                    useValue: options
                },
                {
                    provide: 'NGX_TAG_MANAGER_DEFAULT_DIMENSIONS',
                    useFactory: TagManagerDefaultDimensionsFactory,
                    deps: [ TAG_MANAGER_MODULE_OPTIONS_TOKEN ]
                },
                {
                    provide: 'NGX_TAG_MANAGER_FILTER_EMAIL',
                    useFactory: TagManagerFilterEmailFactory,
                    deps: [ TAG_MANAGER_MODULE_OPTIONS_TOKEN ]
                },
                {
                    provide: 'NGX_TAG_MANAGER_FILTER_PHONE',
                    useFactory: TagManagerFilterPhoneFactory,
                    deps: [ TAG_MANAGER_MODULE_OPTIONS_TOKEN ]
                },
                {
                    provide: 'NGX_TAG_MANAGER_FILTER_QUERY',
                    useFactory: TagManagerFilterQueryFactory,
                    deps: [ TAG_MANAGER_MODULE_OPTIONS_TOKEN ]
                }
            ]
        };
    }
}
